import React, { useState, useEffect, useCallback } from 'react';
import { X, Clock, User, Package, Calendar, CheckCircle, Search, Scan, AlertTriangle, Banknote, CreditCard, FileText, UserCheck, MapPin, Box, Mail, ChevronDown, Keyboard, Users } from 'lucide-react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Reservation } from '../../types';
import { PaymentMethod, PAYMENT_METHOD_CONFIGS } from '../../types/pos';
import { PaymentMethodSelector } from './PaymentMethodSelector';
import { PickListDisplay } from './PickListDisplay';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { getAdminId } from '../../utils/getAdminId';

interface WorkerContact {
  id: string;
  name: string;
  email: string;
  job_function?: string;
}

interface EnhancedPickupModalProps {
  isOpen: boolean;
  onClose: () => void;
  reservations: Reservation[];
  onPickup: (reservation: Reservation, paymentMethod: PaymentMethod, workerName?: string, email?: string) => void;
  processing: boolean;
  products?: any[];
  colors?: any[];
  sizes?: any[];
}

export const EnhancedPickupModal: React.FC<EnhancedPickupModalProps> = ({
  isOpen,
  onClose,
  reservations,
  onPickup,
  processing,
  products = [],
  colors = [],
  sizes = []
}) => {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedReservation, setSelectedReservation] = useState<Reservation | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
  const [workerName, setWorkerName] = useState('');
  const [step, setStep] = useState<'select' | 'payment'>('select');
  const [invoiceEmail, setInvoiceEmail] = useState('');
  const [emailMode, setEmailMode] = useState<'contacts' | 'manual'>('contacts');
  const [workerContacts, setWorkerContacts] = useState<WorkerContact[]>([]);
  const [contactsLoading, setContactsLoading] = useState(false);

  const adminId = getAdminId(user);

  useEffect(() => {
    if (isOpen && adminId) {
      fetchWorkerContacts();
    }
  }, [isOpen, adminId]);

  const fetchWorkerContacts = async () => {
    if (!adminId) return;
    setContactsLoading(true);
    try {
      const { data } = await supabase
        .from('worker_contacts')
        .select('id, name, email, job_function')
        .eq('admin_id', adminId)
        .order('name');
      if (data) setWorkerContacts(data.filter(c => c.email));
    } catch (err) {
      console.error('Failed to fetch worker contacts:', err);
    } finally {
      setContactsLoading(false);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setSearchQuery('');
      setSelectedReservation(null);
      setPaymentMethod('cash');
      setWorkerName('');
      setStep('select');
      setInvoiceEmail('');
      setEmailMode('contacts');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedReservation?.customer_email) {
      setInvoiceEmail(selectedReservation.customer_email);
      setEmailMode('manual');
    }
  }, [selectedReservation]);

  const handleBarcodeInput = useCallback((e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && searchQuery.trim()) {
      const found = reservations.find(r =>
        r.reservation_number.toLowerCase() === searchQuery.toLowerCase().trim()
      );
      if (found) {
        setSelectedReservation(found);
        setStep('payment');
      }
    }
  }, [searchQuery, reservations]);

  if (!isOpen) return null;

  const filteredReservations = reservations.filter(r =>
    r.customer_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.reservation_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    r.customer_phone?.includes(searchQuery) ||
    r.customer_email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'reserved':
        return 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300';
      case 'ready':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300';
    }
  };

  const getExpirationInfo = (expiresAt: string) => {
    const expires = new Date(expiresAt);
    const now = new Date();
    const diffTime = expires.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays <= 0) return { text: 'Expired', urgent: true };
    if (diffDays === 1) return { text: 'Expires tomorrow', urgent: true };
    if (diffDays <= 3) return { text: `Expires in ${diffDays} days`, urgent: true };
    return { text: `Expires ${expires.toLocaleDateString()}`, urgent: false };
  };

  const handleSelectReservation = (reservation: Reservation) => {
    setSelectedReservation(reservation);
    setStep('payment');
  };

  const handleBack = () => {
    setStep('select');
    setSelectedReservation(null);
  };

  const handlePickup = () => {
    if (selectedReservation) {
      if (paymentMethod === 'worker_pickup' && !workerName.trim()) {
        alert('Please enter worker name for internal pickup');
        return;
      }
      onPickup(selectedReservation, paymentMethod, workerName.trim() || undefined, invoiceEmail.trim() || undefined);
    }
  };

  const getProductDetails = (productId: string) => {
    const product = products.find(p => p.id === productId);
    if (!product) return null;

    const color = colors.find(c => c.id === product.color_id);
    const size = sizes.find(s => s.id === product.size_id);

    return { product, color, size };
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-3xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {step === 'select' ? 'Pick Up Reservation' : 'Complete Pickup'}
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                {step === 'select'
                  ? 'Search by reservation number, customer name, or scan barcode'
                  : `Processing pickup for ${selectedReservation?.customer_name}`
                }
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {step === 'select' && (
            <div className="mt-4 relative">
              <div className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2">
                <Scan className="h-4 w-4 text-teal-500" />
                <Search className="h-4 w-4 text-gray-400" />
              </div>
              <input
                type="text"
                placeholder="Scan barcode or search by name/number..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={handleBarcodeInput}
                autoFocus
                className="w-full pl-14 pr-4 py-3 bg-gray-50 dark:bg-gray-900 border-2 border-teal-200 dark:border-teal-800 rounded-xl text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>
          )}
        </div>

        {step === 'select' ? (
          <div className="flex-1 overflow-y-auto p-6">
            {filteredReservations.length === 0 ? (
              <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                <Package className="h-16 w-16 mx-auto mb-4 opacity-50" />
                <p className="text-lg font-medium">No reservations found</p>
                <p className="text-sm mt-2">
                  {searchQuery ? 'Try a different search term' : 'No active reservations to pick up'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {filteredReservations.map(reservation => {
                  const expInfo = getExpirationInfo(reservation.expires_at);

                  return (
                    <button
                      key={reservation.id}
                      onClick={() => handleSelectReservation(reservation)}
                      className="w-full p-4 rounded-xl border-2 border-gray-200 dark:border-gray-700 hover:border-teal-300 dark:hover:border-teal-600 bg-white dark:bg-gray-800 text-left transition-all hover:shadow-md"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-gray-900 dark:text-gray-100">
                              {reservation.reservation_number}
                            </span>
                            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(reservation.status)}`}>
                              {reservation.status.charAt(0).toUpperCase() + reservation.status.slice(1)}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 mt-1 text-sm text-gray-600 dark:text-gray-400">
                            <User className="h-4 w-4" />
                            <span className="font-medium">{reservation.customer_name}</span>
                          </div>
                          {reservation.customer_phone && (
                            <span className="text-xs text-gray-500 dark:text-gray-400 ml-5">
                              {reservation.customer_phone}
                            </span>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="text-xl font-bold text-gray-900 dark:text-gray-100">
                            {'\u20AC'}{reservation.total_amount.toFixed(2)}
                          </div>
                          <div className={`text-xs mt-1 flex items-center justify-end gap-1 ${
                            expInfo.urgent ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'
                          }`}>
                            {expInfo.urgent && <AlertTriangle className="h-3 w-3" />}
                            <Clock className="h-3 w-3" />
                            {expInfo.text}
                          </div>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-3.5 w-3.5" />
                          Reserved: {new Date(reservation.reserved_at).toLocaleDateString()}
                        </div>
                        <div className="flex items-center gap-1">
                          <Package className="h-3.5 w-3.5" />
                          {reservation.reservation_items?.length || 0} item(s)
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto p-6">
            {selectedReservation && (
              <div className="space-y-6">
                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-start justify-between mb-4">
                    <div>
                      <div className="text-sm text-gray-500 dark:text-gray-400">Reservation</div>
                      <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                        {selectedReservation.reservation_number}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm text-gray-500 dark:text-gray-400">Total Amount</div>
                      <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                        {'\u20AC'}{selectedReservation.total_amount.toFixed(2)}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-3 p-3 bg-white dark:bg-gray-800 rounded-lg">
                    <div className="w-10 h-10 bg-teal-500 rounded-full flex items-center justify-center text-white font-semibold">
                      {selectedReservation.customer_name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div className="font-semibold text-gray-900 dark:text-gray-100">
                        {selectedReservation.customer_name}
                      </div>
                      {selectedReservation.customer_phone && (
                        <div className="text-sm text-gray-500 dark:text-gray-400">
                          {selectedReservation.customer_phone}
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedReservation.reservation_items && selectedReservation.reservation_items.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                        Items ({selectedReservation.reservation_items.length})
                      </div>
                      <div className="space-y-2">
                        {selectedReservation.reservation_items.map((item, idx) => {
                          const details = getProductDetails(item.product_id);
                          return (
                            <div key={idx} className="flex justify-between text-sm p-2 bg-white dark:bg-gray-800 rounded">
                              <div className="flex items-center gap-2">
                                <span className="text-gray-600 dark:text-gray-400">{item.quantity}x</span>
                                <span className="text-gray-900 dark:text-gray-100">
                                  {details?.product?.name || 'Product'}
                                  {details?.product?.reference_code && (
                                    <span className="ml-2 text-gray-500 dark:text-gray-400 font-normal">{details.product.reference_code}</span>
                                  )}
                                </span>
                                {details?.color && (
                                  <div
                                    className="w-3 h-3 rounded-full border border-gray-300"
                                    style={{ backgroundColor: details.color.hex_code || '#ccc' }}
                                    title={details.color.name}
                                  />
                                )}
                                {details?.size && (
                                  <span className="text-xs bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                                    {details.size.name}
                                  </span>
                                )}
                              </div>
                              <span className="font-medium text-gray-900 dark:text-gray-100">
                                {'\u20AC'}{item.total_price.toFixed(2)}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>

                <PickListDisplay
                  items={selectedReservation.reservation_items?.map(item => ({
                    product_id: item.product_id,
                    product_name: getProductDetails(item.product_id)?.product?.name,
                    quantity: item.quantity
                  })) || []}
                  title="Pick Locations"
                  showPrintButton={true}
                />

                <PaymentMethodSelector
                  selectedMethod={paymentMethod}
                  onSelect={setPaymentMethod}
                  workerName={workerName}
                  onWorkerNameChange={setWorkerName}
                  disabled={processing}
                />

                <div className="bg-gray-50 dark:bg-gray-900 rounded-xl p-4 border border-gray-200 dark:border-gray-700">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Mail className="h-4 w-4 text-blue-500" />
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Invoice Email (optional)
                      </span>
                    </div>
                    <button
                      onClick={() => {
                        setEmailMode(emailMode === 'contacts' ? 'manual' : 'contacts');
                        if (emailMode === 'manual') setInvoiceEmail('');
                      }}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition-colors"
                    >
                      {emailMode === 'contacts' ? (
                        <><Keyboard className="h-3 w-3" /> Type manually</>
                      ) : (
                        <><Users className="h-3 w-3" /> Select from contacts</>
                      )}
                    </button>
                  </div>

                  {emailMode === 'contacts' ? (
                    <div className="relative">
                      <select
                        value={invoiceEmail}
                        onChange={(e) => setInvoiceEmail(e.target.value)}
                        disabled={contactsLoading}
                        className="w-full appearance-none pl-3 pr-8 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-blue-500 focus:border-transparent cursor-pointer"
                      >
                        <option value="">-- No email (skip) --</option>
                        {workerContacts.map(contact => (
                          <option key={contact.id} value={contact.email}>
                            {contact.name}{contact.job_function ? ` (${contact.job_function})` : ''} — {contact.email}
                          </option>
                        ))}
                      </select>
                      <ChevronDown className="absolute right-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  ) : (
                    <input
                      type="email"
                      placeholder="customer@example.com"
                      value={invoiceEmail}
                      onChange={(e) => setInvoiceEmail(e.target.value)}
                      className="w-full px-3 py-2.5 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm text-gray-900 dark:text-gray-100 placeholder:text-gray-400 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  )}

                  {invoiceEmail && (
                    <p className="mt-2 text-xs text-blue-600 dark:text-blue-400 flex items-center gap-1">
                      <CheckCircle className="h-3 w-3" />
                      Invoice will be emailed to {invoiceEmail}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
          {step === 'select' ? (
            <Button variant="secondary" onClick={onClose} className="w-full">
              Cancel
            </Button>
          ) : (
            <div className="flex gap-3">
              <Button variant="secondary" onClick={handleBack} disabled={processing} className="flex-1">
                Back
              </Button>
              <Button
                variant="primary"
                onClick={handlePickup}
                disabled={processing || (paymentMethod === 'worker_pickup' && !workerName.trim())}
                loading={processing}
                className="flex-1"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Complete Pickup
                <span className="ml-2 font-bold">
                  {'\u20AC'}{selectedReservation?.total_amount.toFixed(2)}
                </span>
              </Button>
            </div>
          )}

          {step === 'payment' && (
            <p className="mt-3 text-xs text-center text-gray-500 dark:text-gray-400">
              This will deduct items from inventory and mark the reservation as fulfilled.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
